import { Check } from 'lucide-react';
import { cn } from '@paulownia/shared/lib/utils';
import { ONBOARDING_STEPS } from '../hooks/useOnboardingState';

interface OnboardingProgressProps {
  currentStep: number;
  onStepClick?: (stepIndex: number) => void;
}

/**
 * Step progress indicator for the onboarding wizard.
 * Displays steps with connecting lines: ● ─ ○ ─ ○
 */
export function OnboardingProgress({ currentStep, onStepClick }: OnboardingProgressProps) {
  return (
    <nav className="w-full" aria-label="Progression de l'inscription">
      <ol className="flex items-center justify-center gap-0">
        {ONBOARDING_STEPS.map((step, index) => {
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep;
          const isClickable = onStepClick && isCompleted;

          return (
            <li key={step.id} className="flex items-center">
              {/* Connector line before (except first step) */}
              {index > 0 && (
                <div
                  className={cn(
                    'w-8 sm:w-12 h-0.5 transition-colors',
                    isCompleted || isCurrent ? 'bg-orange-500' : 'bg-gray-200'
                  )}
                  aria-hidden="true"
                />
              )}

              {/* Step indicator */}
              <button
                type="button"
                onClick={() => isClickable && onStepClick(index)}
                disabled={!isClickable}
                className={cn(
                  'flex items-center justify-center rounded-full transition-all',
                  'w-8 h-8 sm:w-10 sm:h-10',
                  isClickable && 'cursor-pointer hover:ring-2 hover:ring-orange-200',
                  !isClickable && 'cursor-default',
                  isCompleted && 'bg-orange-500 text-white',
                  isCurrent && 'bg-orange-500 text-white ring-4 ring-orange-100',
                  !isCompleted && !isCurrent && 'bg-gray-200 text-gray-400'
                )}
                aria-current={isCurrent ? 'step' : undefined}
                aria-label={`Étape ${index + 1}: ${step.label}${isCompleted ? ' (terminée)' : isCurrent ? ' (en cours)' : ''}`}
              >
                {isCompleted ? (
                  <Check className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={3} />
                ) : (
                  <span className="text-sm font-semibold">{index + 1}</span>
                )}
              </button>
            </li>
          );
        })}
      </ol>

      {/* Step label display */}
      <div className="text-center mt-3">
        <p className="text-sm text-gray-500">
          Étape {currentStep + 1}/{ONBOARDING_STEPS.length}
        </p>
        <p className="text-lg font-semibold text-gray-900">
          {ONBOARDING_STEPS[currentStep].label}
        </p>
      </div>
    </nav>
  );
}
